<!--
 * @Author: zhubincong
 * @Date: 2020-3-9 19:41:31
 * @Desc: 预警整体状况统计
 -->
<template>
  <ViewPanel title="预警整体状况统计" :active="active">
    <div v-show="!noData">
      <div ref="chart" class="chart"></div>
      <ul class="chart-legend">
        <li v-for="(item, i) of warnStatusList" :key="item.statusName" class="chart-legend-item">
          <i :class="['chart-legend-marker', 'marker-' + i]"></i>
          <span class="chart-legend-label">{{ item.statusName }}</span>
          <span class="chart-legend-number">{{ (+item.number).toLocaleString() }}</span>
        </li>
      </ul>
    </div>
    <div v-show="noData" class="no-data">
      暂无数据
    </div>
  </ViewPanel>
</template>

<script>
import ViewPanel from '../Components/ViewPanel.vue'
import G2 from '@antv/g2'
import * as api from '@/api'
import utils from '@/utils'
import riskMonitoringMixins from '@/mixins/riskMonitoringMixins'

const colorList = ['#ffb023', '#00ffed', '#027fe1', '#ff3d1c']

export default {
  name: 'WarnStatus',
  components: { ViewPanel },
  inject: ['$riskMonitor'],
  mixins: [riskMonitoringMixins],
  props: {
    geoData: {
      type: Object,
      default: () => {}
    },

    // 屏幕尺寸类型
    sizeMode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      warnStatusList: [],
      total: '0',

      // 图表尺寸
      chartSize: {
        sm: {
          height: 140,
          width: 140,
          innerRadius: 0.8,
          radius: 0.85
        },
        md: {
          height: 174,
          width: 174,
          innerRadius: 0.7,
          radius: 1
        },
        lg: {
          height: 210,
          width: 210,
          innerRadius: 0.6,
          radius: 1
        }
      },
      chart: null
    }
  },
  computed: {
    active() {
      return this.$riskMonitor.activePanel.toLowerCase() === this.$options.name.toLowerCase()
    },
    /**  显示没有数据 */
    noData() {
      return this.warnStatusList.length === 0
    }
  },
  watch: {
    sizeMode(data) {
      this.changeChartSize()
    }
  },
  async mounted() {
    this.chart = null // g2 chart对象
    this.mapData = null // 地图数据
    await this.getData()
    this.$nextTick(() => {
      // 不使用nextTic，IE有兼容性问题
      this.renderChart()
    })
  },
  methods: {
    /**
     * 查询预警状态维度的统计
     * @author zhubincong
     * @date 2020-03-16 17:22:40
     */
    async getData() {
      const { data } = await api.emmsRiskMonitorQueryAlterEventStatus({
        data: { projectCode: this.$store.state.projectCode }
      })
      this.warnStatusList = data.list.filter(n => n.number)
      this.total = (+data.list.reduce((pre, curr) => pre + curr.number, 0)).toLocaleString()
    },
    /**
     * 查询预警状态维度的地图统计
     * @author zhubincong
     * @date 2020-03-16 17:22:40
     */
    async getMapData() {
      const { data } = await api.emmsRiskMonitorQueryAlterEventStatusMap({
        data: { projectCode: this.$store.state.projectCode }
      })
      // 随机生成区域内经纬度
      this.mapData = utils.getRandomPoint4List(data.list, this.geoData)
    },
    /**
     * 渲染环形图
     * @author zhubincong
     * @date 2020-03-16 18:41:54
     */
    renderChart() {
      const { width, height, innerRadius, radius } = this.chartSize[this.sizeMode]
      const chart = new G2.Chart({
        container: this.$refs.chart,
        // forceFit: true,
        width,
        height,
        padding: 'auto'
      })
      this.chart = chart
      // 数据
      chart.source(this.warnStatusList)
      // 坐标系
      chart.coord('theta', {
        radius,
        innerRadius
      })
      // 不显示图例自己绘制
      chart.legend(false)
      // 不显示tooltip
      chart.tooltip(false)
      // 用于绘制图标的辅助元素
      this.setGuide()
      // 几何标记
      chart
        .intervalStack()
        .position('number')
        .color('statusName', [
          'l(0) 0:#FFB023 1:#FFF823',
          'l(0) 0:#99FFFE 1:#00FFED',
          'l(0) 0:#027FE1 1:#6BB0FF',
          'l(0) 0:#FF3D1C 1:#FF721C'
        ])
        .size(16)
        .shape('slicePath')
        .opacity(0.7)
        .select(false)
        .active(false)
      chart.render()
    },
    /**
     * 渲染地图
     * @author zhubincong
     * @date 2020-03-11 18:47:37
     */
    async renderMap() {
      if (!this.mapData) {
        // 获取地图点位数据
        await this.getMapData()
      }
      const { mapChart, mapView } = this.$riskMonitor
      // 清除其他业务图表
      mapView.clear()
      if (this.mapData.length === 0) {
        mapChart.repaint()
        return
      }
      mapView.source(this.mapData)
      mapView
        .point()
        .position('longitude*latitude')
        .size(8)
        .opacity(0.8)
        .shape('diamond')
        .color('statusName', colorList)
        .tooltip('statusName', value => ({ name: '状态', value }))
      this.configLegend(mapChart)
      mapChart.repaint()
    },
    /**
     * 刷新数据
     * @author zhubincong
     * @date 2020-03-18 17:12:42
     */
    async refreshData() {
      await this.getData()
      this.chart.guide().clear()
      this.setGuide()
      this.chart.changeData(this.warnStatusList)
      this.mapData = null
      if (this.active) this.renderMap()
    },

    /**
     * @author xuchen
     * @date 2020-06-16 18:40:01
     * @desc 设置预警总数
     */
    setGuide() {
      this.chart.guide().html({
        position: ['50%', '50%'],
        html: `<div style="text-align: center;"><p class="guide-total" style="font-size: 34px;color: #fff;font-weight:bold">${this.total}</p><span class="guide-text" style="color:#8feeff;font-size:18px">预警总数</span></div>`,
        alignX: 'middle',
        alignY: 'middle'
      })
    },

    /**
     * @author xuchen
     * @date 2020-04-26 19:46:58
     * @desc 修改环形图大小
     */
    changeChartSize() {
      if (this.chart) {
        const { width, height, innerRadius, radius } = this.chartSize[this.sizeMode]
        this.chart.changeSize(width, height)
        this.chart.coord('theta', {
          radius,
          innerRadius
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.chart {
  background-image: url('~@/assets/images/RiskMonitoring/cycle-chart-bg.png');
  background-repeat: no-repeat;
}
.chart-legend {
  width: 155px;
  position: absolute;
  top: 62px;
  right: 16px;
}
.chart-legend-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  + .chart-legend-item {
    margin-top: 12px;
  }
}
.chart-legend-marker {
  display: inline-block;
  height: 2px;
  width: 10px;
  margin-right: 10px;
}
.marker-0 {
  background-color: #ffb023;
  box-shadow: 0 0 6px 0 #ffb023;
}
.marker-1 {
  background-color: #00ffed;
  box-shadow: 0 0 6px 0 #00ffed;
}
.marker-2 {
  background-color: #027fe1;
  box-shadow: 0 0 6px 0 #027fe1;
}
.marker-3 {
  background-color: #ff3d1c;
  box-shadow: 0 0 6px 0 #ff3d1c;
}
.chart-legend-label {
  color: #ffffff;
}
.chart-legend-number {
  color: rgba(255, 255, 255, 0.9);
  flex-grow: 1;
  text-align: right;
}
.no-data {
  background: url('~@/assets/images/RiskMonitoring/nodata1.png') no-repeat top 11px left 5px;
  background-size: contain;
  padding-right: 80px;
  // height: 216px;
  font-size: 24px;
  color: #5f98a2;
  line-height: 224px;
  text-align: right;
}
</style>

<style lang="scss" scoped>
/* 屏幕尺寸小于1600px */
@media screen and (max-width: 1599px) {
  .view-panel {
    width: 306px;
    height: 208px;
  }
  .chart {
    height: 150px;
    background-position: -5px -2px;
    background-size: 150px 150px;
  }
  .chart-legend-item {
    font-size: 10px;
  }
  .chart-legend {
    top: 33px;
    width: 126px;
  }
  ::v-deep {
    .guide-total {
      padding: 3px 0;
      font-size: 20px !important;
    }
    .guide-text {
      font-size: 14px !important;
    }
  }
  .no-data {
    height: 150px;
    line-height: 150px;
    padding-right: 25px;
    text-align: right;
    background-position: left;
    font-size: 20px;
  }
}

/* 屏幕尺寸大于1600px */
@media screen and (min-width: 1600px) and (max-width: 1899px) {
  .view-panel {
    width: 383px;
    height: 260px;
  }
  .chart {
    height: 186px;
    background-position: -3px -3px;
    background-size: 180px 186px;
  }
  .chart-legend-item {
    font-size: 12px;
  }
  ::v-deep {
    .guide-total {
      font-size: 30px !important;
    }
    .guide-text {
      font-size: 18px !important;
    }
  }
  .no-data {
    height: 186px;
    line-height: 186px;
    padding-right: 45px;
    text-align: right;
    background-position: left;
  }
}

/* 屏幕尺寸大于1900px */
@media screen and (min-width: 1900px) {
  .view-panel {
    height: 312px;
  }
  .chart {
    height: 230px;
    background-position: -3px -3px;
    background-size: auto;
  }
  ::v-deep {
    .guide-total {
      font-size: 34px !important;
    }
    .guide-text {
      font-size: 20px !important;
    }
  }
}
</style>
